import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Music } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

function fmtDuration(seconds) {
  if (!seconds && seconds !== 0) return "\u2014";
  const m = Math.floor(seconds / 60); const s = Math.round(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function Read({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className="text-[13.5px] mt-0.5">{value || <span className="text-muted-foreground italic">\u2014</span>}</div>
    </div>
  );
}

export default function BetaTrackDetail() {
  const { id } = useParams();
  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/beta/catalog/${id}`)
      .then((r) => { if (!cancelled) setT(r.data); })
      .catch((e) => toast.error(errMsg(e, "Could not load work.")))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  if (loading) return <div className="text-sm text-muted-foreground" data-testid="beta-detail-loading">Loading\u2026</div>;
  if (!t) return (
    <div data-testid="beta-detail-empty">
      <Button asChild variant="ghost" size="sm" className="-ml-3"><Link to="/beta"><ArrowLeft className="w-3.5 h-3.5 mr-1"/> Back to catalog</Link></Button>
      <Card className="mt-3"><CardContent className="py-10 text-center text-sm text-muted-foreground">This work is not currently available in the beta catalog.</CardContent></Card>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="beta-detail-page">
      <Button asChild variant="ghost" size="sm" className="-ml-3" data-testid="beta-detail-back">
        <Link to="/beta"><ArrowLeft className="w-3.5 h-3.5 mr-1"/> Back to catalog</Link>
      </Button>

      <Card>
        <CardHeader>
          <CardTitle className="text-2xl font-display flex items-center gap-3">
            <Music className="w-6 h-6 opacity-80" />
            <span data-testid="beta-detail-title">{t.track_title}</span>
          </CardTitle>
          <CardDescription className="text-[13.5px]" data-testid="beta-detail-artist">
            {t.artist || "\u2014"}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid sm:grid-cols-2 gap-4" data-testid="beta-detail-body">
          <Read label="Genre"     value={t.genre} testid="beta-detail-genre" />
          <Read label="Sub-genre" value={t.sub_genre} testid="beta-detail-subgenre" />
          <Read label="Mood"      value={(t.mood_tags || []).join(", ")} testid="beta-detail-mood" />
          <Read label="BPM"       value={t.bpm} testid="beta-detail-bpm" />
          <Read label="Duration"  value={fmtDuration(t.duration)} testid="beta-detail-duration" />
          <Read label="Best use / suitability" value={t.best_use} testid="beta-detail-bestuse" />
          <Read label="Instrumental available" value={t.instrumental_available ? "Yes" : "No"} testid="beta-detail-instrumental" />
          <Read label="Clean / explicit" value={t.clean_available ? "Clean version available" : (t.explicit_available ? "Explicit" : "\u2014")} testid="beta-detail-clean" />
          <div className="sm:col-span-2">
            <Read label="Short description" value={t.short_description} testid="beta-detail-description" />
          </div>
          <div className="sm:col-span-2">
            <Badge
              variant="outline"
              className="text-[10px] uppercase tracking-wide"
              style={{
                background: t.clearance_readiness === "Ready for industry review" ? "hsl(135 35% 90%)" : "hsl(40 60% 92%)",
                color: t.clearance_readiness === "Ready for industry review" ? "hsl(135 35% 22%)" : "hsl(40 60% 24%)",
              }}
              data-testid="beta-detail-clearance"
            >
              <CheckCircle2 className="w-3 h-3 mr-1" /> {t.clearance_readiness}
            </Badge>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button asChild data-testid="beta-detail-feedback">
          <Link to={`/beta/feedback?track=${t.id}`}>Give feedback on this work</Link>
        </Button>
      </div>
    </div>
  );
}
